import { message } from "antd";
import axios, { AxiosResponse, InternalAxiosRequestConfig, AxiosError } from "axios";
import {
  LocalTokens,
  clearAllTokens,
  getActiveAccessToken,
  getCurrentLoginKind,
  isTokenExpired,
} from "@/common/auth/tokens";
import {
  getPassgateRefreshToken,
  savePassgateTokens,
} from "@/common/auth/passgateTokens";
import { withBasePath } from "@/common/basePath";
import { LOCAL_DEV_AUTH } from "@/common/auth/localDevAuth";

export class ApiBusinessError extends Error {
  constructor(message: string, public code: number, public data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiBusinessError';
  }
}

const api = axios.create({
  baseURL: import.meta.env.VITE_SIM_PLATFORM_API_URL || "/api",
  responseType: "json",
  withCredentials: !LOCAL_DEV_AUTH,
  headers: { "Content-Type": "application/json" },
});

const rawAxios = axios.create({
  baseURL: import.meta.env.VITE_SIM_PLATFORM_API_URL || "/api",
  responseType: "json",
  headers: { "Content-Type": "application/json" },
});

// ---- Token 续期：双轨单例，避免并发请求触发多次 refresh ----

let refreshPromise: Promise<string | null> | null = null;

async function refreshLocalOnce(): Promise<string | null> {
  const refreshToken = LocalTokens.getRefresh();
  if (!refreshToken) return null;
  try {
    const res = await rawAxios.post("/auth/refresh", { refreshToken });
    const data = res.data?.data;
    const newAccess: string | undefined = data?.accessToken;
    const newRefresh: string | undefined = data?.refreshToken;
    if (!newAccess) return null;
    LocalTokens.save(newAccess, newRefresh);
    return newAccess;
  } catch {
    return null;
  }
}

async function refreshPassgateOnce(): Promise<string | null> {
  const refreshToken = getPassgateRefreshToken();
  if (!refreshToken) return null;
  try {
    const res = await rawAxios.post("/auth/passgate/refresh", { refreshToken });
    const data = res.data?.data;
    const newAccess: string | undefined = data?.access_token;
    const newRefresh: string | undefined = data?.refresh_token;
    if (!newAccess) return null;
    savePassgateTokens(newAccess, newRefresh);
    return newAccess;
  } catch {
    return null;
  }
}

async function doRefresh(): Promise<string | null> {
  if (LOCAL_DEV_AUTH) return null;
  const kind = getCurrentLoginKind();
  if (kind === "local") return refreshLocalOnce();
  if (kind === "passgate") return refreshPassgateOnce();
  return null;
}

async function ensureFreshToken(): Promise<string | null> {
  if (LOCAL_DEV_AUTH) return null;
  const token = getActiveAccessToken();
  if (token && !isTokenExpired(token)) return token;
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// ---- Request interceptor（主动检测 exp） ----

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (LOCAL_DEV_AUTH) {
    config.headers.delete('Authorization');
    config.withCredentials = false;
    return config;
  }
  const token = await ensureFreshToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---- Response interceptor（被动兜底 401） ----

const isOnLoginPage = () => window.location.pathname.includes("user-login");

const redirectToLogin = () => {
  if (LOCAL_DEV_AUTH || isOnLoginPage()) return;
  clearAllTokens();
  const origin = window.location.pathname + window.location.search;
  window.location.replace(withBasePath(`/user-login?origin=${encodeURIComponent(origin)}`));
};

api.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data } = response;
    const code = data?.code;

    if (!LOCAL_DEV_AUTH && code === 401 && !isOnLoginPage()) {
      redirectToLogin();
      return Promise.reject(new Error("未登录或登录已过期"));
    }

    if (code !== undefined && code !== null && code !== 0 && code !== 200) {
      const rawMsg = data?.msg || data?.message;
      const msg = [rawMsg, code ? `(${code})` : ""].filter(Boolean).join(" ").trim() || "请求失败";
      if (!(response.config as any)?._skipErrorHandler) message.error(msg);
      return Promise.reject(new ApiBusinessError(msg, code, data?.data));
    }
    return response;
  },
  async (err: AxiosError<{ message?: string; msg?: string }>) => {
    const originalRequest = err.config;
    if (LOCAL_DEV_AUTH && err.response?.status === 401) {
      if (!(originalRequest as any)?._skipErrorHandler) message.error('本机免登录 Mock 后端拒绝请求，请核对后端 local-dev 身份配置。');
      return Promise.reject(err);
    }
    if (
      err.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as any)._retried
    ) {
      (originalRequest as any)._retried = true;
      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
      redirectToLogin();
      return Promise.reject(new Error("未登录或登录已过期"));
    }
    if (err.response?.status === 401) {
      redirectToLogin();
      return Promise.reject(new Error("未登录或登录已过期"));
    }
    const data = err.response?.data;
    if (!originalRequest || !(originalRequest as any)?._skipErrorHandler) {
      const msg = data?.message || data?.msg || err.message || "网络错误";
      message.error(msg);
    }
    return Promise.reject(err);
  },
);

const cleanParams = (params: Record<string, any>) => {
  const result: Record<string, any> = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== "") {
      result[key] = value;
    }
  });
  return result;
};

export { api, cleanParams };
